/**
 * UserMenu — sidebar footer user dropdown.
 *
 * Restyle (Phase 1.8): sun→coral avatar gradient, serif name, role badge,
 * coastal separators, coral-700 sign-out. Logout logic preserved.
 */

import {
  User,
  Settings,
  LogOut,
  HelpCircle,
  Moon,
  Sun,
  Globe,
  ChevronDown,
  ArrowLeftRight,
  Coins,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useUnreadCounts } from '@/modules/notifications';
import { useTranslation } from '@/core';
import { useLocationUrl } from '@/shared/hooks/useLocationUrl';
import { cn } from '@/lib/utils';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useAuth } from '@/core/auth/useAuth';
import {
  getAccessibleLocationIds,
  getRoleLabels,
  hasAllLocationAccess,
  hasAnyPermission,
  hasPermission,
} from '@/core/auth/permissions';
import { useLocationStaffTokenConfig, StaffTokenGrantFlowDialog } from '@/modules/staff';
import { ConfirmDialog } from '@/shared/components/ConfirmDialog';
import { useTheme } from '@/theme';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/theme/components/ui/dropdown-menu';
import { Switch } from '@/theme/components/ui/switch';
import { supportedLanguages, changeLanguage, type SupportedLanguage } from '@/core/i18n/i18n';

interface UserMenuProps {
  collapsed?: boolean;
}

const languageLabels: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
};

const languageFlags: Record<SupportedLanguage, string> = {
  en: '🇺🇸',
  es: '🇪🇸',
};

const AVATAR_GRADIENT: React.CSSProperties = {
  backgroundImage: 'linear-gradient(135deg, hsl(var(--sun-400)), hsl(var(--coral-500)))',
};

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function UserMenu({ collapsed = false }: UserMenuProps) {
  const { t, i18n } = useTranslation();
  const locUrl = useLocationUrl();
  const currentLanguage = i18n.language as SupportedLanguage;
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [showGrantTokens, setShowGrantTokens] = useState(false);
  const { data: unreadCounts } = useUnreadCounts();
  const notificationCount = unreadCounts?.total ?? 0;
  const { clearActiveContext, activeLocationId } = useActiveContext();
  const { user } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const accessibleLocationIds = getAccessibleLocationIds(user);

  const showContextSwitch = !!user
    && (user.companyAccess || hasAllLocationAccess(user) || accessibleLocationIds.length > 1);

  // Manual token grant entry point — only for managers who can edit staff at
  // the active location, and only when the location has manual grants enabled.
  // Gate the config fetch behind the permission check so users without staff
  // access never trigger a 403 on the RBAC-restricted config endpoint.
  const canEditStaff = !!user && (
    hasAnyPermission(user, ['company.manage_users', 'company.full_access'], null)
    || hasPermission(user, 'location.edit_staff', activeLocationId)
  );
  const { data: staffTokenConfig } = useLocationStaffTokenConfig(
    canEditStaff ? activeLocationId : null,
  );
  const canGrantTokens = canEditStaff
    && !!activeLocationId
    && !!staffTokenConfig?.enabled
    && !!staffTokenConfig?.manualGrantsEnabled;

  const handleSwitchContext = () => {
    clearActiveContext();
    navigate('/select', { replace: true });
    setShowSwitchConfirm(false);
  };

  const handleLanguageChange = (lang: SupportedLanguage) => {
    // Use the wrapped changeLanguage so the locale bundle is loaded
    // before i18next flips the active language.
    void changeLanguage(lang);
  };

  const displayName = user?.name || t('common.user', 'User');
  const displayEmail = user?.email || '';
  const roleLabels = getRoleLabels(user);
  const roleLabel = roleLabels.length ? roleLabels.join(' · ') : t('common.noRole', 'No role');

  const isDarkMode = resolvedTheme === 'dark';

  const handleDarkModeToggle = (checked: boolean) => {
    setTheme(checked ? 'dark' : 'light');
  };

  const initials = getInitials(displayName);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={displayName}
          className={cn(
            'inline-flex items-center gap-2 rounded-full border border-sand-200 bg-card py-[3px] pl-[3px] pr-2 transition-colors',
            'hover:border-sand-300 hover:bg-sand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ocean-500/40',
            collapsed && 'pr-[3px]',
          )}
        >
          <div className="relative shrink-0">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full font-serif text-[11px] font-semibold text-sand-50"
              style={AVATAR_GRADIENT}
              aria-hidden="true"
            >
              {initials}
            </div>
            {notificationCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -right-0.5 -top-0.5 inline-flex h-3.5 min-w-[14px] items-center justify-center rounded-full border-[1.5px] border-card bg-coral-500 px-1 font-mono text-[9px] font-semibold text-sand-50"
              >
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </div>
          {!collapsed && (
            <>
              <div className="hidden min-w-0 flex-col items-start text-left leading-[1.15] sm:flex">
                <span className="truncate text-[12px] font-semibold text-sand-900">{displayName}</span>
                <span className="truncate font-mono text-[9.5px] uppercase tracking-[0.06em] text-sand-500">
                  {roleLabel}
                </span>
              </div>
              <ChevronDown className="h-3 w-3 shrink-0 text-sand-500" aria-hidden="true" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={8} className="w-64 overflow-hidden p-0">
        {/* Header — gradient ocean+sand strip per the mockup. */}
        <div className="flex items-center gap-3 border-b border-sand-200 bg-gradient-to-br from-ocean-50 to-sand-50 px-4 py-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-serif text-sm font-semibold text-sand-50 shadow-sm"
            style={AVATAR_GRADIENT}
            aria-hidden="true"
          >
            {initials}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-serif text-[14px] font-semibold tracking-[-0.005em] text-sand-900">
              {displayName}
            </span>
            {displayEmail && (
              <span className="truncate font-mono text-[11px] text-muted-foreground">{displayEmail}</span>
            )}
            <span className="mt-1 inline-flex w-fit items-center rounded-full bg-sand-100 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.08em] text-sand-700">
              {roleLabel}
            </span>
          </div>
        </div>

        {/* Primary items */}
        <div className="p-1">
          <DropdownMenuItem asChild>
            <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
              <User className="h-4 w-4 text-sand-500" aria-hidden="true" />
              <span>{t('userMenu.profile', 'My profile')}</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link to={locUrl("/location/settings")} className="flex items-center gap-2 cursor-pointer">
              <Settings className="h-4 w-4 text-sand-500" aria-hidden="true" />
              <span>{t('userMenu.settings', 'Settings')}</span>
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem asChild>
            <Link to="/help" className="flex items-center gap-2 cursor-pointer">
              <HelpCircle className="h-4 w-4 text-sand-500" aria-hidden="true" />
              <span>{t('userMenu.help', 'Help & support')}</span>
            </Link>
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator className="bg-sand-200" />

        {/* Language + dark mode */}
        <div className="p-1">
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-sand-500" aria-hidden="true" />
              <span>{t('userMenu.language', 'Language')}</span>
              <span className="ml-auto text-xs text-sand-500">{languageFlags[currentLanguage]}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {supportedLanguages.map((lang) => (
                <DropdownMenuItem
                  key={lang}
                  onClick={() => handleLanguageChange(lang)}
                  className={cn('cursor-pointer', currentLanguage === lang && 'bg-sand-100 text-sand-900')}
                >
                  <span className="mr-2">{languageFlags[lang]}</span>
                  {languageLabels[lang]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuItem
            onSelect={(e) => e.preventDefault()}
            className="flex items-center justify-between cursor-pointer"
          >
            <span className="flex items-center gap-2">
              {isDarkMode ? (
                <Moon className="h-4 w-4 text-sand-500" aria-hidden="true" />
              ) : (
                <Sun className="h-4 w-4 text-sand-500" aria-hidden="true" />
              )}
              <span>{t('userMenu.darkMode', 'Dark mode')}</span>
            </span>
            <Switch checked={isDarkMode} onCheckedChange={handleDarkModeToggle} className="ml-auto" />
          </DropdownMenuItem>
        </div>

        <DropdownMenuSeparator className="bg-sand-200" />

        {(canGrantTokens || showContextSwitch) && (
          <div className="p-1">
            {canGrantTokens && (
              <DropdownMenuItem
                onClick={() => setShowGrantTokens(true)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Coins className="h-4 w-4 text-sand-500" aria-hidden="true" />
                <span>{t('userMenu.grantTokens', 'Grant tokens')}</span>
              </DropdownMenuItem>
            )}
            {showContextSwitch && (
              <DropdownMenuItem
                onClick={() => setShowSwitchConfirm(true)}
                className="flex items-center gap-2 cursor-pointer"
              >
                <ArrowLeftRight className="h-4 w-4 text-sand-500" aria-hidden="true" />
                <span>{t('gateway.switchContext', 'Switch context')}</span>
              </DropdownMenuItem>
            )}
          </div>
        )}

        {/* Sign-out rail — coral-tinted footer per the mockup. */}
        <DropdownMenuItem asChild className="rounded-none border-t border-coral-200 bg-coral-50 px-4 py-2.5 text-coral-700 focus:bg-coral-100 focus:text-coral-700">
          <Link
            to="/logout"
            className="flex w-full items-center gap-2 text-[13px] font-semibold"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            <span>{t('userMenu.logout', 'Sign out')}</span>
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>

      <ConfirmDialog
        open={showSwitchConfirm}
        onOpenChange={setShowSwitchConfirm}
        title={t('gateway.switchConfirmTitle', 'Switch context?')}
        description={t(
          'gateway.switchConfirmDescription',
          'You are about to leave the current context and return to the selector. Unsaved changes will be lost.',
        )}
        confirmLabel={t('gateway.switchConfirmAction', 'Switch')}
        cancelLabel={t('common.cancel', 'Cancel')}
        destructive={false}
        onConfirm={handleSwitchContext}
      />

      {canGrantTokens && (
        <StaffTokenGrantFlowDialog
          open={showGrantTokens}
          locationId={activeLocationId}
          onOpenChange={setShowGrantTokens}
        />
      )}
    </DropdownMenu>
  );
}
